import { useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'workscore-assessment-state';

const defaultState = {
  assessmentId: null,
  assessmentSlug: null,
  attemptId: null,
  questionSet: null,
  questionsPerSection: 10,
  currentSection: 1,
  currentQuestionIndex: 0,
  answers: [],
  violations: [],
  startTime: null,
  sectionStartTimes: {},
  isActive: false
};

function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...defaultState };
    const parsed = JSON.parse(raw);
    return { ...defaultState, ...parsed };
  } catch {
    return { ...defaultState };
  }
}

let state = loadInitial();
const listeners = new Set();

function persist() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function emit() {
  for (const listener of listeners) listener(state);
}

function setState(updater) {
  const next = typeof updater === 'function' ? updater(state) : { ...state, ...updater };
  state = next;
  persist();
  emit();
}

function getState() {
  return state;
}

const actions = {
  startAssessment: (assessmentId, assessmentSlug = assessmentId) => {
    const now = new Date().toISOString();
    setState({
      assessmentId,
      assessmentSlug,
      attemptId: null,
      questionSet: null,
      questionsPerSection: 10,
      currentSection: 1,
      currentQuestionIndex: 0,
      answers: [],
      violations: [],
      startTime: now,
      sectionStartTimes: { 1: now },
      isActive: true
    });
  },

  setAttemptId: (attemptId) => {
    setState({ attemptId });
  },

  setQuestionSet: (questionSet, questionsPerSection = 10) => {
    setState({
      questionSet: questionSet || null,
      questionsPerSection: Number(questionsPerSection || 10)
    });
  },

  setCurrentSection: (section) => {
    setState((prev) => ({
      ...prev,
      currentSection: section,
      currentQuestionIndex: 0,
      sectionStartTimes: {
        ...prev.sectionStartTimes,
        [section]: prev.sectionStartTimes[section] || new Date().toISOString()
      }
    }));
  },

  setCurrentQuestionIndex: (index) => {
    setState({ currentQuestionIndex: Math.max(0, index) });
  },

  recordAnswer: (answer) => {
    setState((prev) => ({
      ...prev,
      answers: [...prev.answers.filter((a) => a.questionId !== answer.questionId), answer]
    }));
  },

  recordViolation: (type) => {
    setState((prev) => {
      const now = new Date().toISOString();
      const existing = prev.violations.find((item) => item.type === type);
      if (existing) {
        return {
          ...prev,
          violations: prev.violations.map((item) => (
            item.type === type
              ? { ...item, count: item.count + 1, timestamps: [...item.timestamps, now], lastOccurred: now }
              : item
          ))
        };
      }

      return {
        ...prev,
        violations: [...prev.violations, { type, count: 1, timestamps: [now], lastOccurred: now }]
      };
    });
  },

  advanceQuestion: () => {
    setState((prev) => ({ ...prev, currentQuestionIndex: prev.currentQuestionIndex + 1 }));
  },

  submitSection: () => {
    setState((prev) => {
      const next = prev.currentSection + 1;
      return {
        ...prev,
        currentSection: next,
        currentQuestionIndex: 0,
        sectionStartTimes: { ...prev.sectionStartTimes, [next]: new Date().toISOString() }
      };
    });
  },

  resetAssessment: () => {
    setState({ ...defaultState });
  }
};

export function useWorkscoreStore() {
  const [snapshot, setSnapshot] = useState(getState());

  useEffect(() => {
    listeners.add(setSnapshot);
    return () => listeners.delete(setSnapshot);
  }, []);

  return useMemo(() => ({ ...snapshot, ...actions }), [snapshot]);
}

export function getWorkscoreState() {
  return getState();
}

export function resetWorkscoreState() {
  actions.resetAssessment();
}
